import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Application, User } from '../types';
import { UserMenu } from '../components/UserMenu';
import { oauthService } from '../oauth';
// Bundled locally (Vite) instead of hotlinked from docs.etas.com — the backend's
// CSP img-src is scoped to 'self' + the docs API origin, so cross-origin images
// are blocked. Importing makes them same-origin build assets.
import etasLogoWhite from '../images/etas-logo-white.svg';
import documentationHero from '../images/documentation.png';

export function Catalog() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadApplications();
    loadUser();

    // Listen for OAuth success messages from popup windows
    function handleMessage(event: MessageEvent) {
      // Verify the message origin for security
      if (event.origin !== window.location.origin) {
        console.warn('Ignoring message from untrusted origin:', event.origin);
        return;
      }

      // Handle OAuth success from popup
      if (event.data?.type === 'OAUTH_SUCCESS') {
        loadUser();
        loadApplications(); // Refresh apps list to update access states
      } else if (event.data?.type === 'OAUTH_ERROR') {
        console.error('❌ OAuth error from popup:', event.data.error);
      }
    }

    // Listen for OAuth success via BroadcastChannel (cross-tab communication)
    function handleBroadcast(event: MessageEvent) {
      if (event.data?.type === 'OAUTH_SUCCESS') {
        loadUser();
        loadApplications(); // Refresh apps list to update access states
      }
    }

      // Listen for app navigation commands (e.g., focus home)
      function handleAppNav(event: MessageEvent) {
        if (event.data?.type === 'NAVIGATE_HOME') {
          try {
            window.focus && window.focus();
          } catch (e) {
            // ignore
          }
          navigate('/');
        }
      }

    window.addEventListener('message', handleMessage);
    
    let broadcastChannel: BroadcastChannel | null = null;
    try {
      broadcastChannel = new BroadcastChannel('auth_channel');
      broadcastChannel.addEventListener('message', handleBroadcast);
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
    }

    let navChannel: BroadcastChannel | null = null;
    try {
      navChannel = new BroadcastChannel('app_nav_channel');
      navChannel.addEventListener('message', (e) => handleAppNav(e as any));
    } catch (err) {
      // ignore if unsupported
    }

    // Cleanup listeners on unmount
    window.addEventListener('message', handleAppNav);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleAppNav);
      if (broadcastChannel) {
        broadcastChannel.close();
      }
      if (navChannel) {
        navChannel.close();
      }
    };
  }, []);

  // Log errors for diagnostics without blocking UI rendering
  useEffect(() => {
    if (error) {
      console.warn('Catalog: loadApplications error (non-blocking):', error);
    }
  }, [error]);

  async function loadUser() {
    try {
      const currentUser = await api.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      // User not logged in - this is fine
    }
  }

  async function loadApplications() {
    try {
      setLoading(true);
      const apps = await api.getApplications();
      setApplications(apps);
    } catch (err) {
      // Keep the page usable even if apps can't be fetched (e.g., mock service not available)
      setError('Failed to load applications');
      console.warn('Failed to load applications (showing static content instead)', err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }

  function handleAppClick(app: Application) {
    // Always open document in new tab
    // DocumentViewer will handle authentication if needed
    openDocumentInNewTab(app);
  }

  function getDocumentIcon(app: Application): { icon: string; label: string } {
    // Public docs always accessible
    if (app.public) {
      return { icon: 'ph-arrow-up-right', label: 'Open' };
    }
    
    // Not authenticated (accessState = NOT_CHECKED)
    if (!user || app.accessState === 'NOT_CHECKED') {
      if (app.protectedByIdentityProviderAllowList) {
        return { icon: 'ph-lock', label: 'Sign in required' };
      }
      if (app.protectedByActiveSubscription) {
        return { icon: 'ph-info', label: 'Sign in required' };
      }
      // Fallback for protected docs
      return { icon: 'ph-lock', label: 'Sign in required' };
    }
    
    // Authenticated
    if (app.accessState === 'ACCESSIBLE') {
      return { icon: 'ph-arrow-up-right', label: 'Open' };
    }
    
    if (app.accessState === 'NOT_ACCESSIBLE') {
      return { icon: 'ph-info', label: 'Not accessible' };
    }
    
    // Fallback
    return { icon: 'ph-info', label: 'Unknown' };
  }

  function handleLogin() {
    oauthService.startLogin('/');
  }

  async function handleLogout() {
    try {
      setLoggingOut(true);

      // Ensure spinner shows for at least 1 second for UX
      const delay = new Promise((res) => setTimeout(res, 1000));

      const logoutPromise = api.logout().catch((err) => {
        console.error('Logout API error:', err);
      });

      await Promise.all([delay, logoutPromise]);

      // Clear user state and navigate home
      setUser(null);
      navigate('/');
    } finally {
      setLoggingOut(false);
    }
  }

  function openDocumentInNewTab(app: Application) {
    window.open(app.entryPath, '_blank', 'noopener,noreferrer');
  }

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
      </div>
    );
  }

  // Do not block rendering on load errors; show static content instead

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      {loggingOut && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.9)',
          zIndex: 10000
        }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: '#164293' }}></i>
        </div>
      )}
      {/* Header */}
      <header className="header-gradient" style={{ 
        height: '56px',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingLeft: '16px',
        paddingRight: '16px',
        boxSizing: 'border-box'
      }}>
        <div className="header-inner" style={{
          width: '100%',
          maxWidth: '1330px',
          margin: '0 auto',
          paddingLeft: '24px',
          paddingRight: '24px',
          boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={etasLogoWhite} 
              alt="ETAS" 
              style={{ height: '16px', marginRight: '16px' }}
            />
            <span className="etas-header-title" style={{ color: 'white' }}>Documentation</span>
          </div>
          
          {/* User menu or Sign In button */}
          <div>
            {user ? (
              <UserMenu user={user} onLogout={handleLogout} />
            ) : (
              <button onClick={handleLogin} className="btn-signin">
                <i className="cui-icon cui-icon-sign-in" aria-hidden="true"></i>
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="catalog-container hero" style={{
        paddingTop: '24px',
        paddingBottom: '16px',
        paddingLeft: '24px',
        paddingRight: '24px',
        alignItems: 'flex-start'
      }}>
        {/* Hero Text */}
        <div className="hero-text" style={{
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: '1.2',
          maxWidth: 'none',
          position: 'relative',
          textAlign: 'left'
        }}>
          <h1 className="etas-heading">ETAS Online Documentation</h1>
          
          <p className="etas-body-text">
            Welcome to our documentation hub. Easily find what you need to support your projects 
            and enhance your team's productivity. We're constantly expanding and updating our library 
            with new products and documentation, so make sure to check in regularly.
            <br /><br />
            If the information you need isn't here, our{' '}
            <a 
              href="https://www.etas.com/ww/en/support-training/support-hotlines/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="etas-link"
            >
              customer support
            </a>
            {' '}team is ready to help or visit our{' '}
            <a 
              href="https://www.etas.com/ww/en/downloads/?path=%2F&page=1&order=asc&layout=table" 
              target="_blank" 
              rel="noopener noreferrer"
              className="etas-link"
            >
              download center
            </a>
            {' '}for further documentation.
          </p>
        </div>

        {/* Hero Image */}
        <div className="hero-image" style={{ 
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
          flex: '1',
          maxWidth: 'none'
        }}>
          <img 
            src={documentationHero}
            alt="Documentation"
            style={{ 
              width: '100%',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>
      </div>

      {/* Divider */}
      <div style={{
        width: 'calc(100% - 32px)',
        maxWidth: 'calc(1330px - 32px)',
        height: '1px',
        backgroundColor: 'var(--divider-color, #bdc1c5)',
        margin: '16px auto'
      }}></div>

      {/* Software Section */}
      <div className="catalog-container" style={{
        paddingTop: '19px',
        paddingBottom: '40px',
        paddingLeft: '24px',
        paddingRight: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
      }}>
        <h2 style={{ 
          fontSize: '20px', 
          color: 'black',
          textAlign: 'left',
          lineHeight: '28px',
          marginBottom: '24px',
          width: '100%',
          fontWeight: '600',
          fontFamily: 'Manrope, sans-serif'
        }}>
          Software
        </h2>

        {/* Software Applications Grid */}
        <div className="app-grid" style={{ width: '100%' }}>
          {applications.filter(app => app.category === 'software').map((app) => (
            <a
              key={app.id}
              onClick={(e) => {
                e.preventDefault();
                handleAppClick(app);
              }}
              href="#"
              className="section-link"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                lineHeight: '24px',
                cursor: 'pointer'
              }}
            >
              {app.name}
              <i className={`ph ${getDocumentIcon(app).icon}`} style={{ fontSize: '16px', color: 'var(--action-icon-color, #164293)' }} title={getDocumentIcon(app).label}></i>
            </a>
          ))}
        </div>
      </div>

      {/* Hardware Section */}
      <div className="catalog-container" style={{
        paddingTop: '37px',
        paddingBottom: '40px',
        paddingLeft: '24px',
        paddingRight: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
      }}>
        <h2 style={{ 
          fontSize: '20px', 
          color: 'black',
          textAlign: 'left',
          lineHeight: '28px',
          marginBottom: '24px',
          width: '100%',
          fontWeight: '600',
          fontFamily: 'Manrope, sans-serif'
        }}>
          Hardware
        </h2>

        {/* Hardware Applications Grid */}
        <div className="app-grid" style={{ width: '100%' }}>
          {applications.filter(app => app.category === 'hardware').map((app) => (
            <a
              key={app.id}
              onClick={(e) => {
                e.preventDefault();
                handleAppClick(app);
              }}
              href="#"
              className="section-link"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                lineHeight: '24px',
                cursor: 'pointer'
              }}
            >
              {app.name}
              <i className={`ph ${getDocumentIcon(app).icon}`} style={{ fontSize: '16px', color: 'var(--action-icon-color, #164293)' }} title={getDocumentIcon(app).label}></i>
            </a>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="footer" style={{ marginTop: 'auto' }}>
        <div style={{
          width: '100%',
          maxWidth: '1330px',
          margin: '0 auto',
          paddingLeft: '24px',
          paddingRight: '24px',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 400, color: 'var(--global-text-secondary-color, #666)' }}>
            © {new Date().getFullYear()} ETAS GmbH
          </div>
          <div style={{ display: 'flex', gap: '24px' }}>
            <a
              href="https://www.etas.com/ww/en/corporate-information/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              Corporate information
            </a>
            <a
              href="https://www.etas.com/ww/en/terms-of-use-legal-notice/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              Terms of use / Legal notice
            </a>
            <a
              href="https://www.etas.com/ww/en/data-protection-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              Data protection notice (privacy policy)
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                (window as any).showDockPrivacySettings();
              }}
              className="footer-link"
              style={{ cursor: 'pointer' }}
            >
              Privacy settings
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
